import { aiCopilotIsOn } from '@editor/hooks/copilot/useIsAiCopilotEnabled';
import { GrowthBook } from '@growthbook/growthbook-react';
import {
  SubscriptionPlanInterval,
  SubscriptionPlanType,
  type SubscriptionCountryPrice,
  type SubscriptionGlobalPrice,
  type SubscriptionPrice,
  type SubscriptionPrices,
} from '@repo/subscription/api';

import {
  CreditLimits,
  InfoIconModalContent,
  PricingConfig,
  PricingPlanComparison,
  PricingPlanAddon,
} from '../types/Pricing';
import { SupportedCurrency } from '../types/SubscriptionPrice';

import { getNewPricingConfig } from './newPricingPageConfig';

// @TODO: This config will be deleted and replaced by the 'newPricingPageConfig.ts' (which will be renamed) as part of the cleanup ticket https://app.clickup.com/t/86bzvq45k.

interface GetComparisonOptions {
  creditLimits?: CreditLimits;
  selectedExpertAddon?: boolean;
}

export const FREE_PLAN_SINGLE_USE_CREDITS_AMOUNT = 20;

export const getUnlimitedInfoIconModalContentData = (
  selectedExpertAddon?: boolean
): InfoIconModalContent | undefined => {
  if (selectedExpertAddon) {
    return {
      title: 'Fair Use Policy',
      content:
        'To prevent abuse and ensure a smooth experience for all Kittl AI users, AI generations are capped at 100 per hour.',
    };
  }
  return undefined;
};

export const getExpertCreditsLabel = (
  credits: number | undefined,
  isFeature: boolean,
  selectedExpertAddon?: boolean,
  showNewPricingPlan?: boolean,
  isWorkspacesEnabled?: boolean
): string => {
  if (selectedExpertAddon) {
    return `Unlimited${isFeature ? ' AI Credits' : ''}`;
  } else if (showNewPricingPlan) {
    return credits
      ? `${credits} Credits ${isWorkspacesEnabled ? '/' : 'per'} month${
          isWorkspacesEnabled ? ' per editor' : ''
        }`
      : '';
  } else {
    return `${credits ?? '?'}${isFeature ? ` AI Credits per month${isWorkspacesEnabled ? ' per editor' : ''}` : `/month${isWorkspacesEnabled ? ' per editor' : ''}`}`;
  }
};

export const getComparison = (
  type: 'FREE' | 'PRO' | 'EXPERT' | 'BUSINESS',
  growthBook?: GrowthBook,
  { creditLimits, selectedExpertAddon }: GetComparisonOptions = {}
): PricingPlanComparison => {
  const isCopilotFeatureOn = aiCopilotIsOn();
  const showNewPricingPage = growthBook?.isOn('new-pricing-page');
  const isWorkspacesEnabled = growthBook?.isOn('workspaces');

  const FREE_COMPARISON: PricingPlanComparison = {
    features: [
      { label: 'Unlimited' },
      { label: '-' },
      { label: showNewPricingPage ? '5' : '20' },
      { label: '500 MB' },
      { label: '1' },
      { label: '-' },
      { label: '-' },
      { label: '-' },
      { label: '-' },
      { label: '-' },
      { label: '-' },
    ],
    aiDesignTools: [
      { label: `${FREE_PLAN_SINGLE_USE_CREDITS_AMOUNT} credits single-use` },
      { label: '-' },
      { check: true },
      { label: '-' },
      { label: '1' },
      ...(isCopilotFeatureOn ? [{ check: true }] : []),
      { check: true },
      { check: true },
      { label: '-' },
      { check: true },
      ...(isCopilotFeatureOn ? [{ check: true }] : []),
      { check: true },
      { check: true },
      { check: true },
      { check: true },
    ],
    licensing: [{ check: true }, { check: true }],
    content: [
      { check: true },
      { check: true },
      { check: true },
      { check: true },
      { label: '-' },
      { label: '-' },
      { label: '-' },
    ],
    businessAddons: [
      { label: '-' },
      { label: '-' },
      { label: '-' },
      { label: '-' },
      { label: '-' },
      { label: '-' },
    ],
  };

  const PRO_COMPARISON: PricingPlanComparison = {
    features: [
      { label: 'Unlimited' },
      { label: '-' },
      { label: showNewPricingPage ? '50' : '100' },
      { label: '10 GB' },
      { label: '3' },
      { check: true },
      { check: true },
      { check: true },
      { check: true },
      { check: true },
      { check: true },
    ],
    aiDesignTools: [
      {
        label: `${creditLimits?.PRO ?? '?'}/month${
          isWorkspacesEnabled ? ' per editor' : ''
        }`,
      },
      { check: true },
      { check: true },
      { check: true },
      { label: '12' },
      ...(isCopilotFeatureOn ? [{ check: true }] : []),
      { check: true },
      { check: true },
      { check: true },
      { check: true },
      ...(isCopilotFeatureOn ? [{ check: true }] : []),
      { check: true },
      { check: true },
      { check: true },
      { check: true },
    ],
    licensing: [{ check: true }, { check: true }],
    content: [
      { check: true },
      { check: true },
      { check: true },
      { check: true },
      { check: true },
      { check: true },
      { check: true },
    ],
    businessAddons: [
      { label: '-' },
      { label: '-' },
      { label: '-' },
      { label: '-' },
      { label: '-' },
      { label: '-' },
    ],
  };

  const EXPERT_COMPARISON: PricingPlanComparison = {
    features: [
      { label: 'Unlimited' },
      { label: 'Unlimited' },
      { label: 'Unlimited' },
      { label: '100 GB' },
      { label: '3' },
      { check: true },
      { check: true },
      { check: true },
      { check: true },
      { check: true },
      { check: true },
    ],
    aiDesignTools: [
      {
        label: getExpertCreditsLabel(
          creditLimits?.EXPERT,
          false,
          selectedExpertAddon,
          undefined,
          isWorkspacesEnabled
        ),
        infoIconModalContent:
          getUnlimitedInfoIconModalContentData(selectedExpertAddon),
      },
      { check: true },
      { check: true },
      { check: true },
      { label: '30' },
      ...(isCopilotFeatureOn ? [{ check: true }] : []),
      { check: true },
      { check: true },
      { check: true },
      { check: true },
      ...(isCopilotFeatureOn ? [{ check: true }] : []),
      { check: true },
      { check: true },
      { check: true },
      { check: true },
    ],
    licensing: [{ check: true }, { check: true }],
    content: [
      { check: true },
      { check: true },
      { check: true },
      { check: true },
      { check: true },
      { check: true },
      { check: true },
    ],
    businessAddons: [
      { label: '-' },
      { label: '-' },
      { label: '-' },
      { label: '-' },
      { label: '-' },
      { label: '-' },
    ],
  };

  const BUSINESS_COMPARISON: PricingPlanComparison = {
    features: [
      { label: 'Unlimited' },
      { label: 'Unlimited' },
      { label: 'Unlimited' },
      { label: 'Custom' },
      { label: '3' },
      { check: true },
      { check: true },
      { check: true },
      { check: true },
      { check: true },
      { check: true },
    ],
    aiDesignTools: [
      { label: 'Custom' },
      { check: true },
      { check: true },
      { check: true },
      { label: 'Custom' },
      ...(isCopilotFeatureOn ? [{ check: true }] : []),
      { check: true },
      { check: true },
      { check: true },
      { check: true },
      ...(isCopilotFeatureOn ? [{ check: true }] : []),
      { check: true },
      { check: true },
      { check: true },
      { check: true },
    ],
    licensing: [{ check: true }, { check: true }],
    content: [
      { check: true },
      { check: true },
      { check: true },
      { check: true },
      { check: true, label: '(high-res)' },
      { check: true },
      { check: true },
    ],
    businessAddons: [
      { contactSales: true },
      { contactSales: true },
      { contactSales: true },
      { contactSales: true },
      { contactSales: true },
      { contactSales: true },
    ],
  };

  return {
    FREE_COMPARISON,
    PRO_COMPARISON,
    EXPERT_COMPARISON,
    BUSINESS_COMPARISON,
  }[`${type}_COMPARISON`];
};

export const getAddons = (): readonly PricingPlanAddon[] => {
  const addons: PricingPlanAddon[] = [
    {
      amount: 0,
      currency: SupportedCurrency.USD,
      generationCredits: 100,
    },
  ];

  return addons;
};

const GLOBAL_CURRENCY = SupportedCurrency.USD;

export const getFindPrice =
  (options: {
    subscriptionPrices: SubscriptionPrices;
    country: string | null;
  }) =>
  (
    plan: SubscriptionPlanType,
    interval: SubscriptionPlanInterval
  ): SubscriptionGlobalPrice | SubscriptionCountryPrice => {
    const { subscriptionPrices, country } = options;

    const subPrice = subscriptionPrices.find(
      (price) => price.interval === interval && price.plan === plan
    );
    if (!subPrice) {
      throw new Error(`No ${interval} price found for ${plan} plan`);
    }

    const countryPrice = country
      ? subPrice.countryPrices.find((price) => price.country === country)
      : null;

    if (countryPrice) {
      return countryPrice;
    }

    const globalPrice = subPrice.globalPrices.find(
      (price) => price.currency === GLOBAL_CURRENCY
    );

    if (!globalPrice) {
      throw new Error(`No ${interval} price found for ${plan} plan`);
    }

    return globalPrice;
  };

export interface GetPricingConfigParams {
  growthBook?: GrowthBook;
  creditLimits?: CreditLimits;
  designLimits?: Record<SubscriptionPlanType, number>;
  selectedExpertAddon?: boolean;
  subscriptionPrices: SubscriptionPrice[];
  country: string | null;
}

const getLegacyPricingConfig = ({
  growthBook,
  creditLimits,
  selectedExpertAddon,
  subscriptionPrices,
  country,
}: GetPricingConfigParams): PricingConfig => {
  const findPrice = getFindPrice({
    subscriptionPrices,
    country,
  });
  const proMonthlyPrice = findPrice(
    SubscriptionPlanType.PRO,
    SubscriptionPlanInterval.monthly
  );
  const proYearlyPrice = findPrice(
    SubscriptionPlanType.PRO,
    SubscriptionPlanInterval.yearly
  );
  const expertMonthlyPrice = findPrice(
    SubscriptionPlanType.EXPERT,
    SubscriptionPlanInterval.monthly
  );
  const expertYearlyPrice = findPrice(
    SubscriptionPlanType.EXPERT,
    SubscriptionPlanInterval.yearly
  );
  return {
    [SubscriptionPlanType.FREE]: {
      title: 'Free',
      description:
        'Perfect to create stunning designs using a template or starting from scratch.',
      buttonLabel: 'Get Free',
      price: {
        monthly: {
          id: '',
          amount: 0,
          currency: proMonthlyPrice.currency,
        },
        yearly: {
          id: '',
          amount: 0,
          currency: proMonthlyPrice.currency,
        },
        label: 'Free forever',
      },
      featuresLabel: 'Free plan includes:',
      mainFeatures: [
        {
          label: `${FREE_PLAN_SINGLE_USE_CREDITS_AMOUNT} AI Credits (single-use)`,
          bold: true,
        },
        { label: 'Work on 20 projects' },
        { label: '500MB Upload space' },
        { label: 'Low-res image export' },
        {
          label: 'Commercial license',
          details: '(requires attribution)',
        },
      ],
      comparison: getComparison('FREE', growthBook, { creditLimits }),
    },
    [SubscriptionPlanType.PRO]: {
      title: 'Pro',
      description:
        'Great for individuals who need access to <b>vector files</b> and <b>advanced editing</b>. ',
      buttonLabel: 'Get Pro',
      price: {
        monthly: {
          id: proMonthlyPrice.id,
          amount: proMonthlyPrice.amount,
          currency: proMonthlyPrice.currency,
        },
        yearly: {
          id: proYearlyPrice.id,
          amount: proYearlyPrice.amount / 12,
          currency: proYearlyPrice.currency,
        },
      },
      featuresLabel: 'Everything FREE has plus:',
      mainFeatures: [
        {
          label: `${creditLimits?.PRO ?? '?'} AI Credits per month`,
          bold: true,
          isGenerationCredits: true,
        },
        { label: 'Work on 100 projects' },
        { label: '10GB Upload space' },
        { label: 'Unlimited vector exports' },
        {
          label: 'All premium templates',
          premium: true,
        },
        { label: 'Full commercial license' },
        { label: 'Advanced AI features' },
      ],
      comparison: getComparison('PRO', growthBook, { creditLimits }),
    },
    [SubscriptionPlanType.EXPERT]: {
      title: 'Expert',
      description:
        'For professionals that need <b>stunning designs without limitations</b>.',
      buttonLabel: 'Get Expert',
      price: {
        monthly: {
          id: expertMonthlyPrice.id,
          amount: expertMonthlyPrice.amount,
          currency: expertMonthlyPrice.currency,
          addons: getAddons(),
        },
        yearly: {
          id: expertYearlyPrice.id,
          amount: expertYearlyPrice.amount / 12,
          currency: expertYearlyPrice.currency,
          addons: getAddons(),
        },
      },
      featuresLabel: 'Everything PRO has plus:',
      mainFeatures: [
        {
          label: getExpertCreditsLabel(
            creditLimits?.EXPERT,
            true,
            selectedExpertAddon
          ),
          bold: true,
          infoIconModalContent:
            getUnlimitedInfoIconModalContentData(selectedExpertAddon),
        },
        {
          label: 'Unlimited Brand Kits',
          premium: true,
        },
        { label: 'Unlimited projects' },
        { label: '100GB Upload space' },
        { label: 'Unlimited vector exports' },
        {
          label: 'All premium templates',
          premium: true,
        },
        { label: 'Full commercial license' },
        { label: 'Advanced AI features' },
      ],
      comparison: getComparison('EXPERT', growthBook, {
        creditLimits,
        selectedExpertAddon,
      }),
    },
    BUSINESS: {
      title: 'Business',
      description:
        'For teams that need a powerful graphic engine to increase efficiency and designs that sell.',
      buttonLabel: 'Get Business',
      price: {
        label: 'Get a personalized plan',
        monthly: { id: '', amount: 0, currency: GLOBAL_CURRENCY },
        yearly: { id: '', amount: 0, currency: GLOBAL_CURRENCY },
      },
      featuresLabel: 'Accelerate your business with exclusive content:',
      mainFeatures: [
        { label: 'Everything in Expert Plan' },
        { label: 'Up to 5 users in one account' },
        { label: 'Advanced customer support' },
        { label: 'Branding & team features' },
      ],
      comparison: getComparison('BUSINESS', growthBook, { creditLimits }),
    },
  };
};

export const getPricingConfig = <T extends PricingConfig>({
  creditLimits,
  designLimits,
  growthBook,
  selectedExpertAddon,
  subscriptionPrices,
  country,
}: GetPricingConfigParams): T => {
  const showNewPricingPage = growthBook?.isOn('new-pricing-page');
  // Returns pricing config based on feature flag

  const getPricingConfig = showNewPricingPage
    ? getNewPricingConfig
    : getLegacyPricingConfig;

  return getPricingConfig({
    creditLimits,
    designLimits,
    growthBook,
    selectedExpertAddon,
    subscriptionPrices,
    country,
  }) as T;
};
